import { addToast } from '@octano/global-ui';
import { useMemo } from 'react';
import { Card, Col, Row } from 'reactstrap';
import { saveStudentFile } from '../../../../../../api/requests/tuitionProcess';
import DynamicForm from '../../../../../../components/DynamicForm/DynamicForm';
import DymanicHealthCareCampus from '../../../../../../components/DynamicForm/parts/DymanicHealthCareCampus';
import DynamicContact from '../../../../../../components/DynamicForm/parts/DynamicContact';
import DynamicContactCampus from '../../../../../../components/DynamicForm/parts/DynamicContactCampus';
import DynamicContactPDI from '../../../../../../components/DynamicForm/parts/DynamicContactPDI';
import DynamicEducationalBackgroundPDI from '../../../../../../components/DynamicForm/parts/DynamicEducationalBackgroundPDI';
import DynamicEmergencyContactPDI from '../../../../../../components/DynamicForm/parts/DynamicEmergencyContactPDI';
import DynamicFamilyContactCampus from '../../../../../../components/DynamicForm/parts/DynamicFamilyContactCampus';
import { useStepState } from '../../../../../../components/step/useStepState';
import { useParameters } from '../../../../../../hooks/useParameters';
import {
  childrensOptions,
  drivingLicenseOptions,
  educationalLevelOptions,
  familyGroupOptions,
  maritalStatusOptions,
  parentalSurvivalOptions,
  sexCIOptions,
  SustainerType,
} from '../../../../../../utils/selectOptions';
import { useTuitionProcess } from '../../../../../TuitionProcess/TuitionProcessContext';
import IdentificationTypeLoader from '../../../../../updateStudentFile/loaders/IdentificationTypeLoader';
import HeaderStep from '../HeaderStep';
import StudentBackgroundNoSua from './parts/StudentBackgroundNoSua';
import StudentBackgroundNoSuaCampus from './parts/StudentBackgroundNoSuaCampus';
import StudentBackgroundNoSuaPDI from './parts/StudentBackgroundNoSuaPDI';
import StudentFileLoader from './parts/StudentFileLoader';

const StudentFileNoSuaStep = () => {
  //Custom Hooks
  const {
    prefix: rootPrefix,
    postulationDetailId,
    onError,
  } = useTuitionProcess();
  const { nextStep } = useStepState();
  const {
    countryOptions,
    institutionTypeOptions,
    communeOptions,
    regionOptions,
  } = useParameters();

  //Step Prefix
  const prefix = useMemo(() => `${rootPrefix}.studentFile`, [rootPrefix]);

  //Submit form function
  const onSubmit = async (values: any) => {
    if (postulationDetailId) {
      const responses = Object.entries(values)
        .map(([key, value]) => ({
          fieldName: key,
          value,
        }))
        .filter(({ value }) => value);

      /**
       * si el país es chile igual mando location y custom city
       */
      if (String(values.country?.label).toLowerCase().includes('chile')) {
        if (values.sustainerType?.value === SustainerType.LegalPerson) {
          responses.push(
            { fieldName: 'location', value: values.businessCommune?.label },
            {
              fieldName: 'customCity',
              value:
                values.businesscustomCity ?? values.businessCity?.label ?? '-',
            },
          );
        } else {
          responses.push(
            { fieldName: 'location', value: values.commune?.label },
            {
              fieldName: 'customCity',
              value: values.customCity ?? values.city?.label ?? '-',
            },
          );
        }
      }

      const { error } = await saveStudentFile(postulationDetailId, {
        responses,
      });

      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: 'Ha ocurrido un error al guardar la información',
        });
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: 'Datos guardados correctamente',
        });
        nextStep();
      }
    }
  };

  const sectionComponents = (photo: string | null) => {
    return {
      postulantInformation: (
        <StudentBackgroundNoSua
          photo={photo}
          key={Math.floor(Math.random() * 100)}
        />
      ),
      postulantInformationCampus: (
        <StudentBackgroundNoSuaCampus
          photo={photo}
          key={Math.floor(Math.random() * 100)}
        />
      ),
      academicContact: (
        <DynamicContact
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactSection`}
          optionalFields={['addressExtra', 'phone']}
          key={Math.floor(Math.random() * 100)}
        />
      ),
      academicContactCampus: (
        <DynamicContactCampus
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactSection`}
          key={Math.floor(Math.random() * 100)}
          hasEmail={true}
        />
      ),
      healthCareCampus: (
        <DymanicHealthCareCampus
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.healtCareSection`}
          key={Math.floor(Math.random() * 100)}
        />
      ),
      familyContactCampus: (
        <DynamicFamilyContactCampus
          type="sustainerFile"
          key={Math.floor(Math.random() * 100)}
          prefix={`${prefix}.familyGroupSection`}
          disabled={false}
          hasEmail
        />
      ),
      postulantInformationPDI: (
        <StudentBackgroundNoSuaPDI
          photo={photo}
          key={Math.floor(Math.random() * 100)}
          readOnly={false}
        />
      ),
      highschoolInformationPDI: (
        <DynamicEducationalBackgroundPDI
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.highschoolInformationSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={false}
        />
      ),
      academicContactPDI: (
        <DynamicContactPDI
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={false}
        />
      ),
      emergencyContactPDI: (
        <DynamicEmergencyContactPDI
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.emergencyContactSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={false}
        />
      ),
    };
  };

  return (
    <Card className="px-4 px-md-5 py-4">
      <Row className="pb-4 pb-md-0">
        <Col xs={12}>
          <HeaderStep prefixStep={prefix} showBackBtn={true} />
        </Col>
      </Row>
      <IdentificationTypeLoader>
        {({ identificationTypeSelect }) => (
          <StudentFileLoader {...{ postulationDetailId, onError }}>
            {({ defaultValues, photo }) => (
              <DynamicForm
                key={1}
                dynamicFormId={1}
                defaultValues={defaultValues}
                sectionComponents={sectionComponents(photo)}
                options={{
                  countryHighSchool: countryOptions,
                  typeHighSchool: institutionTypeOptions,
                  regionFamily: regionOptions,
                  communeFamily: communeOptions,
                  healthCareSystem: regionOptions,
                  healthCareProvider: communeOptions,
                  fatherEducationalLevel: educationalLevelOptions,
                  motherEducationalLevel: educationalLevelOptions,
                  familyGroup: familyGroupOptions,
                  parentalSurvival: parentalSurvivalOptions,
                  hasChildrens: childrensOptions,
                  hasDrivingLicense: drivingLicenseOptions,
                  maritalStatus: maritalStatusOptions,
                  sexID: sexCIOptions,
                  emergencyIdentificationType: identificationTypeSelect,
                }}
                onSubmit={onSubmit}
              />
            )}
          </StudentFileLoader>
        )}
      </IdentificationTypeLoader>
    </Card>
  );
};

export default StudentFileNoSuaStep;
